import intersection from 'lodash.intersection';

import {
  ANNE_ARUNDEL_ORG_DEFAULT_GRADE,
  DEFAULT_GRADE,
  GRADES_6_AND_ABOVE,
  VALID_CLASS_GRADES,
  VALID_MIDDLE_SCHOOL_CLASS_GRADES,
} from 'src/constants/grades.constants';
import { ANNE_ARUNDEL_ORG_SOURCED_ID } from 'src/constants/onboarding.constants';
import { Class } from 'src/interfaces/class.types';
import { ClassGrades, GRADES } from 'src/interfaces/grades.types';
import { Org } from 'src/interfaces/onboarding.types';
import { isOrgMember } from 'src/utils/onboarding.utils';
import { pipe } from 'src/utils/pipe';

type GradesAndOrgsInfo = {
  grades: (ClassGrades | string)[];
  userOrgs?: Org[];
};

const getAllUserGrades = (
  classes: Class[],
  userOrgs?: Org[],
): GradesAndOrgsInfo => {
  const allUserGrades = classes.map((classObj) => classObj.grades).flat();

  const hasGCClasses = classes.find(
    (classObj) => classObj.metadata.isGoogleClassroomCourse,
  );

  // If a user has the GC class but no grades, set the 'Other' grade which will cause the MS view to show up.
  if (hasGCClasses && !allUserGrades.length) {
    return {
      grades: ['Other'],
      userOrgs,
    };
  }

  return {
    grades: allUserGrades,
    userOrgs,
  };
};

export const isRelevantGrade = (grade: ClassGrades | string) =>
  Object.keys(VALID_CLASS_GRADES).includes(grade as ClassGrades) ||
  grade === 'Other';

export const filterRelevantGrades = ({
  grades,
  userOrgs,
}: GradesAndOrgsInfo) => ({
  grades: grades.filter(isRelevantGrade),
  userOrgs,
});

type SortGradesDescReturn = {
  grades: ClassGrades[];
  hasOtherGrade: boolean;
  userOrgs?: Org[];
};

export const sortGradesDesc = ({
  grades,
  userOrgs,
}: GradesAndOrgsInfo): SortGradesDescReturn => {
  return {
    grades: intersection(
      Object.keys(VALID_CLASS_GRADES).reverse(),
      grades,
    ) as ClassGrades[],
    hasOtherGrade: grades.includes('Other'),
    userOrgs,
  };
};

export const getDefaultGrade = ({
  grades,
  hasOtherGrade,
  userOrgs,
}: SortGradesDescReturn): GRADES => {
  const lowestGrade = VALID_CLASS_GRADES[grades[grades.length - 1]];
  const userMiddleSchoolGrades = intersection(
    grades,
    Object.keys(VALID_MIDDLE_SCHOOL_CLASS_GRADES).reverse(),
  );

  const hasMiddleSchoolGrades = userMiddleSchoolGrades.length > 0;
  const hasNoGradesOnClassLevel = grades.length === 0;

  if (
    hasNoGradesOnClassLevel &&
    isOrgMember(ANNE_ARUNDEL_ORG_SOURCED_ID, userOrgs)
  ) {
    return ANNE_ARUNDEL_ORG_DEFAULT_GRADE;
  }

  if (hasMiddleSchoolGrades || hasOtherGrade) {
    return GRADES.SIXTH;
  }

  if (hasNoGradesOnClassLevel) {
    return DEFAULT_GRADE;
  }

  return lowestGrade;
};

export const calculateUserGradeClassLevel = pipe(
  getAllUserGrades,
  filterRelevantGrades,
  sortGradesDesc,
  getDefaultGrade,
);

export const calculateUserGradeOrgLevel = pipe(
  filterRelevantGrades,
  sortGradesDesc,
  getDefaultGrade,
);

export const isDefaultGradeUser = (userGrades: GRADES[]): boolean => {
  return userGrades.length === 1 && userGrades[0] === DEFAULT_GRADE;
};

export const hasOnlyHigherMiddleSchoolGrades = (
  userGrades: GRADES[],
): boolean => {
  return (
    userGrades.length > 0 &&
    userGrades.every((grade) => GRADES_6_AND_ABOVE.includes(grade))
  );
};

export const parseGradeString = (grade: string): GRADES[] => {
  if (grade.includes(';')) {
    return grade.split(';') as GRADES[];
  }
  return [grade as GRADES];
};

export const hasMatchingGrade = (
  gradeString: string,
  targetGrades: GRADES[],
): boolean => {
  const grades = parseGradeString(gradeString);
  return grades.some((grade) => targetGrades.includes(grade));
};

export const normalizeOnboardingGrades = (grades: string[]): GRADES[] => {
  return grades
    .map((grade) => {
      // Handle special case: 'K' → 'KG' for kindergarten
      if (grade === 'K') {
        return VALID_CLASS_GRADES['KG'];
      }

      // If it's a single-digit number, try with leading zero first
      if (/^\d$/.test(grade)) {
        const paddedGrade = grade.padStart(2, '0');
        return VALID_CLASS_GRADES[paddedGrade as ClassGrades];
      }

      // Try the grade as-is (handles two-digit format like '06', '07')
      return VALID_CLASS_GRADES[grade as ClassGrades];
    })
    .filter(Boolean) as GRADES[];
};
